import { motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { BrandedSpinner } from '@/components/common/BrandedLoading';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/contexts/AuthContext';

const LOCAL_USER_INFO_KEY = 'five_ai_local_user_info';

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

function persistEmail(email: string) {
  try {
    localStorage.setItem(
      LOCAL_USER_INFO_KEY,
      JSON.stringify({ email, signUpEmail: email })
    );
  } catch {
    // ignore
  }
}

export const Hero = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const trimmed = email.trim();
  const emailValid = isValidEmail(trimmed);

  const handleMeetCoach = async () => {
    setError(null);
    if (!trimmed) {
      setError('Please enter your email.');
      return;
    }
    if (!emailValid) {
      setError('Please enter a valid email address.');
      return;
    }
    setChecking(true);
    const enteredEmailLower = trimmed.toLowerCase();
    const currentUserEmail = (user?.email ?? '').trim().toLowerCase();
    const isDifferentUser = isAuthenticated && currentUserEmail && enteredEmailLower !== currentUserEmail;

    if (isDifferentUser) {
      await logout();
    }
    persistEmail(enteredEmailLower);
    // No profile-by-email check here. Onboarding start API decides: REDIRECT_LOGIN (existing user) or session (new/resume).
    navigate(`/onboarding?email=${encodeURIComponent(enteredEmailLower)}`);
    setChecking(false);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background">
      {/* Same layered gradient pattern as Features / About */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />

      <div className="container relative mx-auto px-4 py-24 sm:py-32 z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-5xl mx-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              AI-Powered Career Transformation
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 leading-[1.1] tracking-tight"
          >
            <span className="text-foreground">Your Personal Career</span>
            <br />
            <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
              Strategist
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            Meet Five, your AI Career Copilot that transforms ambitious
            professionals into influential leaders. Master essential skills with
            personalized 5-minute daily actions.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex flex-col gap-2 max-w-2xl mx-auto"
          >
            <div className="flex flex-col sm:flex-row gap-0 justify-center">
              <div className="flex-1 relative">
                <Input
                  type="email"
                  placeholder="Enter your email *"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleMeetCoach();
                    }
                  }}
                  className="h-14 px-6 text-base rounded-2xl sm:rounded-r-none border-2 bg-secondary/30 backdrop-blur-sm"
                  aria-required="true"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'hero-email-error' : undefined}
                />
              </div>
              <Button
                size="lg"
                onClick={handleMeetCoach}
                disabled={!trimmed || !emailValid || checking}
                className="h-14 text-base px-8 rounded-2xl sm:rounded-l-none bg-gradient-to-r from-primary to-primary/80 hover:opacity-90 transition-opacity shadow-lg hover:shadow-xl group whitespace-nowrap disabled:opacity-70"
                aria-label="Meet your AI coach – email required"
              >
                {checking ? (
                  <>
                    <BrandedSpinner sizeClassName="h-5 w-5" className="shrink-0" />
                    <span className="ml-2">Checking…</span>
                  </>
                ) : (
                  <>
                    Meet Your AI Career Copilot
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </div>
            {error && (
              <p id="hero-email-error" className="text-sm text-destructive text-center sm:text-left">
                {error}
              </p>
            )}
            <p className="text-xs text-muted-foreground text-center sm:text-left">
              Email is required to start. We’ll take you to your coach if you’re already onboarded, or to onboarding if not.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-12 flex items-center justify-center gap-8 text-sm text-muted-foreground"
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span>No credit card</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span>No hidden charges</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span>Cancel anytime</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

